import React, { useEffect } from 'react';
import { Container, Typography, Box, Grid, Paper, createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocalFloristOutlinedIcon from '@mui/icons-material/LocalFloristOutlined';

const defaultTheme = createTheme({
    palette: {
        primary: { main: '#e88ba9' },
        background: { default: '#fffafb' },
        text: { primary: '#5c434a', secondary: '#8c7077' },
    },
    typography: {
        fontFamily: '"Outfit", "Inter", sans-serif',
        h2: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
        h4: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 700 },
        h5: { fontFamily: '"Playfair Display", serif', color: '#5c434a', fontWeight: 600 },
    },
});

const floatAnimation = keyframes`
  0% { transform: translateY(0px) scale(1); }
  50% { transform: translateY(-10px) scale(1.02); }
  100% { transform: translateY(0px) scale(1); }
`;

const FeatureCard = styled(Paper)({
    borderRadius: '24px',
    border: '1px solid #fcf4f4',
    boxShadow: '0 10px 30px rgba(232, 139, 169, 0.08)',
    padding: '32px',
    background: '#fff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 15px 40px rgba(232, 139, 169, 0.15)',
        borderColor: '#e88ba9',
    }
});

const About = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <ThemeProvider theme={defaultTheme}>
            <CssBaseline />
            <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '120px', pb: 10 }}>
                <Container maxWidth="lg">

                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 8, position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', width: 100, height: 100, borderRadius: '50%', background: 'rgba(232, 139, 169, 0.05)', zIndex: 0 }} />
                        <Typography variant="h2" component="h1" sx={{ position: 'relative', zIndex: 1, mb: 3, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
                            Our Sweet Journey
                        </Typography>
                        <Typography sx={{ color: '#8c7077', fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto', lineHeight: 1.8 }}>
                            Welcome to <strong>Slice of Sweet</strong>! We specialize in crafting the finest cakes and cupcakes for all your special moments. Our mission is to deliver joy through delicious, beautifully crafted creations that turn every celebration into an unforgettable memory.
                        </Typography>
                    </Box>

                    {/* Main Story Image & Text */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -20,
                                        left: -20,
                                        right: 20,
                                        bottom: 20,
                                        background: 'linear-gradient(135deg, #ffedf4 0%, #fffafb 100%)',
                                        borderRadius: '32px',
                                        zIndex: 0
                                    }
                                }}
                            >
                                <Box
                                    component="img"
                                    src="https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?auto=format&fit=crop&q=80&w=800"
                                    alt="Cake Making"
                                    sx={{
                                        width: '100%',
                                        borderRadius: '32px',
                                        position: 'relative',
                                        zIndex: 1,
                                        boxShadow: '0 20px 40px rgba(232, 139, 169, 0.2)',
                                        animation: `${floatAnimation} 6s ease-in-out infinite`
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h4" sx={{ mb: 3, color: '#5c434a' }}>
                                Baked With Love
                            </Typography>
                            <Typography sx={{ color: '#5c434a', fontSize: '1.1rem', lineHeight: 1.8, mb: 3 }}>
                                Whether it's a grand wedding, a milestone birthday, or just a casual Friday treat, we believe every slice tells a story.
                            </Typography>
                            <Typography sx={{ color: '#8c7077', fontSize: '1.1rem', lineHeight: 1.8 }}>
                                We use the highest quality, locally-sourced ingredients to ensure our products taste as breathtaking as they look. Every single cake is made from scratch with unparalleled creativity and meticulous attention to detail, ensuring you receive a one-of-a-kind masterpiece every time you order.
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Why Choose Us */}
                    <Typography variant="h4" sx={{ textAlign: 'center', mb: 6 }}>
                        Why Choose Us?
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={4}>
                            <FeatureCard elevation={0}>
                                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: '#ffedf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                    <FavoriteBorderIcon sx={{ fontSize: 40, color: '#e88ba9' }} />
                                </Box>
                                <Typography variant="h5" sx={{ mb: 2 }}>Premium Ingredients</Typography>
                                <Typography sx={{ color: '#8c7077', lineHeight: 1.6 }}>
                                    We never cut corners. Every cake is baked using fresh, locally sourced ingredients and genuine butter for the richest flavor.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FeatureCard elevation={0}>
                                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: '#ffedf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#e88ba9' }} />
                                </Box>
                                <Typography variant="h5" sx={{ mb: 2 }}>Endless Customization</Typography>
                                <Typography sx={{ color: '#8c7077', lineHeight: 1.6 }}>
                                    From towering wedding cakes to specialized dietary requirements, we offer a wide variety of customizable options.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FeatureCard elevation={0}>
                                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: '#ffedf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                    <LocalFloristOutlinedIcon sx={{ fontSize: 40, color: '#e88ba9' }} />
                                </Box>
                                <Typography variant="h5" sx={{ mb: 2 }}>Eco-Friendly</Typography>
                                <Typography sx={{ color: '#8c7077', lineHeight: 1.6 }}>
                                    We are dedicated to sustainability. All our treats are delivered in beautiful, 100% eco-friendly and biodegradable packaging.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                    </Grid>

                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default About;
